// Utility for bridging web <-> native (ReactNativeWebView)
// Use for share, download, etc. Fallbacks to browser if not in WebView.

export function triggerNativeShare({ title, fileKey, filename }: { title: string; fileKey: string; filename?: string }) {
  // Extract filename from fileKey if not provided
  const extractedFilename = filename || extractFilenameFromFileKey(fileKey);
  
  if (typeof window !== 'undefined' && (window as any).ReactNativeWebView) {
    (window as any).ReactNativeWebView.postMessage(
      JSON.stringify({
        type: 'share',
        payload: { title, fileKey, filename: extractedFilename },
      })
    );
  }
}

// Helper function to extract filename from fileKey
function extractFilenameFromFileKey(fileKey: string): string {
  // fileKey format: "memberId/uuid_filename.pdf"
  const parts = fileKey.split('/');
  return parts[parts.length - 1]; // Returns "uuid_filename.pdf"
}

export function triggerNativeEmergencyShare({ memberId, memberName }: { memberId: string, memberName: string }) {
  if (typeof window !== 'undefined' && (window as any).ReactNativeWebView) {
    (window as any).ReactNativeWebView.postMessage(
      JSON.stringify({
        type: 'emergency_share',
        payload: { memberId: memberId, memberName: memberName },
      })
    );
  }
}

export function triggerNativeHealthSummaryShare({ memberId, memberName }: { memberId: string, memberName: string }) {
  if (typeof window !== 'undefined' && (window as any).ReactNativeWebView) {
    (window as any).ReactNativeWebView.postMessage(
      JSON.stringify({
        type: 'health_summary_share',
        payload: { memberId: memberId, memberName: memberName },
      })
    );
  }
}

export function triggerNativeDownload({ fileKey, filename }: { fileKey: string; filename: string }) {
  if (typeof window !== 'undefined' && (window as any).ReactNativeWebView) {
    (window as any).ReactNativeWebView.postMessage(
      JSON.stringify({
        type: 'download',
        payload: { fileKey, filename },
      })
    );
  }
}

// Send login tokens directly to React Native WebView
export function triggerNativeLogin(token: string, member: any) {
  if (typeof window !== 'undefined' && (window as any).ReactNativeWebView) {
    (window as any).ReactNativeWebView.postMessage(
      JSON.stringify({ type: 'login', payload: { token, member } })
    );
  }
}


// Send logout event to React Native WebView
export function triggerNativeLogout() {
  if (typeof window !== 'undefined' && (window as any).ReactNativeWebView) {
    (window as any).ReactNativeWebView.postMessage(
      JSON.stringify({ type: 'logout' })
    );
  }
}

// Send offline document clear event to React Native WebView
export function triggerNativeOfflineDoc(payload?: any) {
  if (typeof window !== 'undefined' && (window as any).ReactNativeWebView) {
    (window as any).ReactNativeWebView.postMessage(
      JSON.stringify({ type: 'offline_doc', payload })
    );
  }
}

// Trigger native camera capture
export function triggerNativeCameraCapture() {
  if (typeof window !== 'undefined' && (window as any).ReactNativeWebView) {
    (window as any).ReactNativeWebView.postMessage(
      JSON.stringify({ 
        type: 'document_upload',
        payload: { source: 'camera' }
      })
    );
  }
}

// Trigger native gallery picker
export function triggerNativeGalleryPick() {
  if (typeof window !== 'undefined' && (window as any).ReactNativeWebView) {
    (window as any).ReactNativeWebView.postMessage(
      JSON.stringify({ 
        type: 'document_upload',
        payload: { source: 'gallery' }
      })
    );
  }
}

// Trigger native file picker
export function triggerNativeFilePick() {
  if (typeof window !== 'undefined' && (window as any).ReactNativeWebView) {
    (window as any).ReactNativeWebView.postMessage(
      JSON.stringify({ 
        type: 'document_upload',
        payload: { source: 'files' }
      })
    );
  }
}

// Google Sign-In trigger for React Native WebView
export function triggerNativeGoogleSignIn() {
  if (typeof window !== 'undefined' && (window as any).ReactNativeWebView && (window as any).ReactNativeWebView.postMessage) {
    (window as any).ReactNativeWebView.postMessage(
      JSON.stringify({ type: 'google_signin' })
    );
  }
}

// Generic reminder event trigger for React Native WebView
export function triggerNativeReminder(action: 'CREATE' | 'UPDATE' | 'DELETE', reminder: any) {
  if (typeof window !== 'undefined' && (window as any).ReactNativeWebView) {
    (window as any).ReactNativeWebView.postMessage(
      JSON.stringify({
        type: 'reminders',
        payload: { action, reminder },
      })
    );
  }
}
// Optionally extend for more actions (camera, etc) as needed

// Notify React Native WebView of profile change
export function triggerNativeProfileChange({ memberId }: { memberId: string }) {
  if (typeof window !== 'undefined' && (window as any).ReactNativeWebView) {
    (window as any).ReactNativeWebView.postMessage(
      JSON.stringify({ type: 'profile_change', payload: { memberId } })
    );
  }
}

// Trigger native in-app purchase flow
export function triggerNativeIAPPurchase(payload: {
  memberId: string;
  familyId: string;
  planId: string;
  planName: string;
  planType: string;
  productId: string;
  price: number;
  currency: string;
}) {
  if (typeof window !== 'undefined' && (window as any).ReactNativeWebView) {
    (window as any).ReactNativeWebView.postMessage(
      JSON.stringify({
        type: 'iap_purchase',
        payload
      })
    );
  }
}

// Trigger native subscription cancellation (opens App Store/Play Store subscription management)
export function triggerNativeIAPCancel() {
  if (typeof window !== 'undefined' && (window as any).ReactNativeWebView) {
    (window as any).ReactNativeWebView.postMessage(
      JSON.stringify({
        type: 'iap_cancel'
      })
    );
  }
}
